/**
 * PaymentModal — Modal de baixa individual.
 * Suporta 3 tipos de pagamento:
 * - FINANCIAL: PIX/TED/Boleto + conta bancária
 * - DEBT_CONFESSION: referência opcional a contrato CD
 * - ASSET_DATION: lista de bens (veículo, maquinário, imóvel, produto, grãos)
 */
import { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { registerPayment } from '../services/settlementApi';
import { PaymentType, paymentTypeLabels, PaymentMethod, SettlementType, BankAccount, AssetDationItem } from '../constants/settlement';
import { toast } from 'react-toastify';
import AssetDationForm from './AssetDationForm';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';

interface Props {
    titleDebt: any;
    onClose: () => void;
    onSuccess: () => void;
}

// valor inicial do <input type="datetime-local"> = agora, no fuso do navegador
const nowLocalInput = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
};

const PaymentModal: React.FC<Props> = ({ titleDebt, onClose, onSuccess }) => {
    const remaining = parseFloat(titleDebt.amount) - parseFloat(titleDebt.paid_amount || '0');

    const [form, setForm] = useState({
        payment_type: PaymentType.FINANCIAL as string,
        amount: remaining.toFixed(2),
        payment_method: PaymentMethod.PIX as string,
        settlement_type: SettlementType.TOTAL as string,
        bank_account: BankAccount.ITAU as string,
        payment_source: 'SACADO',
        payment_date: nowLocalInput(),
        is_subrogation: false,
        pending_guarantor_refund: false,
        notes: '',
        // Debt confession
        debt_confession_contract: '',
        // Asset dation
        asset_dation_list: [] as AssetDationItem[],
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        const amount = parseFloat(form.amount);
        if (!amount || amount <= 0) {
            toast.error('Informe um valor maior que zero');
            return;
        }
        if (form.payment_type === PaymentType.ASSET_DATION && form.asset_dation_list.length === 0) {
            toast.error('Adicione pelo menos um bem para dação');
            return;
        }
        setSubmitting(true);
        try {
            await registerPayment(titleDebt.title_debt_id ?? titleDebt.id, {
                amount,
                // datetime-local é wall-clock local → toISOString envia o instante em UTC (tz-aware)
                payment_date: form.payment_date ? new Date(form.payment_date).toISOString() : undefined,
                payment_type: form.payment_type,
                payment_method: form.payment_type === PaymentType.FINANCIAL ? form.payment_method : undefined,
                settlement_type: form.settlement_type,
                bank_account: form.payment_type === PaymentType.FINANCIAL ? form.bank_account : undefined,
                payment_source: form.payment_source,
                is_subrogation: form.is_subrogation,
                pending_guarantor_refund: form.pending_guarantor_refund,
                notes: form.notes,
                debt_confession_contract: form.payment_type === PaymentType.DEBT_CONFESSION ? form.debt_confession_contract : undefined,
                asset_dation_list: form.payment_type === PaymentType.ASSET_DATION ? form.asset_dation_list : undefined,
            });
            toast.success('Baixa registrada com sucesso');
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Erro ao registrar baixa: ${e.message}`);
        }
        setSubmitting(false);
    };

    const assetTotal = form.asset_dation_list.reduce((sum, item) => sum + (item.estimated_value || 0), 0);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Registrar Baixa</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                {/* Context */}
                <div className="mb-4 text-sm bg-gray-50 dark:bg-slate-700 p-3 rounded">
                    <div className="grid grid-cols-2 gap-2">
                        <p><span className="text-gray-500">Contrato:</span> <strong>{titleDebt.contract_number}</strong></p>
                        <p><span className="text-gray-500">Parcela:</span> <strong>{titleDebt.document_title || titleDebt.installment_number || '-'}</strong></p>
                        <p><span className="text-gray-500">Valor:</span> <strong>{formatBRL(titleDebt.amount)}</strong></p>
                        <p><span className="text-gray-500">Vencimento:</span> <strong>{formatDateBR(titleDebt.due_date)}</strong></p>
                        <p><span className="text-gray-500">Já pago:</span> <strong>{formatBRL(titleDebt.paid_amount || '0')}</strong></p>
                        <p><span className="text-gray-500">Saldo:</span> <strong className="text-red-600">{formatBRL(remaining)}</strong></p>
                    </div>
                </div>

                {/* Payment Type Selector */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Pagamento</label>
                    <div className="grid grid-cols-3 gap-2">
                        {Object.entries(paymentTypeLabels).map(([key, label]) => (
                            <button key={key} type="button"
                                className={`px-3 py-2 text-sm rounded border transition-colors ${
                                    form.payment_type === key
                                        ? 'bg-blue-500 text-white border-blue-500'
                                        : 'bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-600 hover:border-blue-300'
                                }`}
                                onClick={() => setForm({ ...form, payment_type: key })}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Form */}
                <div className="space-y-3">
                    {/* Amount + Settlement Type (all types) */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor do Pagamento (R$)</label>
                            <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de Liquidação</label>
                            <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.settlement_type} onChange={e => setForm({ ...form, settlement_type: e.target.value })}>
                                <option value={SettlementType.TOTAL}>Total</option>
                                <option value={SettlementType.PARCIAL}>Parcial</option>
                                <option value={SettlementType.ANTECIPADO}>Antecipado (desconto)</option>
                                <option value={SettlementType.ATRASO}>Em atraso (com juros)</option>
                            </select>
                        </div>
                    </div>

                    {/* Data/hora do pagamento (all types) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data/Hora do Pagamento</label>
                        <input type="datetime-local" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} />
                    </div>

                    {/* FINANCIAL fields */}
                    {form.payment_type === PaymentType.FINANCIAL && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meio de Pagamento</label>
                                <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                    value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                                    <option value={PaymentMethod.PIX}>PIX</option>
                                    <option value={PaymentMethod.TED}>TED</option>
                                    <option value={PaymentMethod.BOLETO}>Boleto</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Conta Bancária</label>
                                <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                    value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })}>
                                    <option value={BankAccount.ITAU}>Itaú</option>
                                    <option value={BankAccount.BB}>Banco do Brasil</option>
                                    <option value={BankAccount.STARKBANK}>Stark Bank</option>
                                    <option value={BankAccount.DAYCOVAL}>Daycoval</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* DEBT_CONFESSION fields */}
                    {form.payment_type === PaymentType.DEBT_CONFESSION && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contrato de Confissão de Dívida (opcional)</label>
                            <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.debt_confession_contract} onChange={e => setForm({ ...form, debt_confession_contract: e.target.value })}
                                placeholder="Ex: CD0033 (busca futura disponível)" />
                            <p className="text-xs text-gray-400 mt-1">Vincule a um contrato CD existente ou deixe em branco para vincular depois.</p>
                        </div>
                    )}

                    {/* ASSET_DATION fields */}
                    {form.payment_type === PaymentType.ASSET_DATION && (
                        <AssetDationForm
                            items={form.asset_dation_list}
                            onChange={items => setForm({ ...form, asset_dation_list: items, amount: items.reduce((s, i) => s + (i.estimated_value || 0), 0).toFixed(2) })}
                        />
                    )}

                    {/* Source + flags (all types) */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fonte do Pagamento</label>
                            <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.payment_source} onChange={e => setForm({ ...form, payment_source: e.target.value })}>
                                <option value="SACADO">Sacado</option>
                                <option value="EMITENTE">Emitente</option>
                                <option value="AVALISTA">Avalista</option>
                                <option value="CREDOR">Credor</option>
                            </select>
                        </div>
                        <div className="flex flex-col justify-end gap-2">
                            <div className="flex items-center gap-2">
                                <input type="checkbox" id="subrogation" className="rounded"
                                    checked={form.is_subrogation} onChange={e => setForm({ ...form, is_subrogation: e.target.checked })} />
                                <label htmlFor="subrogation" className="text-sm text-gray-700 dark:text-gray-300">Sub-rogação</label>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" id="pending_guarantor_refund" className="rounded"
                                    checked={form.pending_guarantor_refund} onChange={e => setForm({ ...form, pending_guarantor_refund: e.target.checked })} />
                                <label htmlFor="pending_guarantor_refund" className="text-sm text-gray-700 dark:text-gray-300">Aval a devolver</label>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações</label>
                        <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                            placeholder="Observações opcionais..." />
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-5 flex justify-between items-center">
                    {form.payment_type === PaymentType.ASSET_DATION && assetTotal > 0 && (
                        <span className="text-sm text-green-600 font-medium">Total bens: {formatBRL(assetTotal)}</span>
                    )}
                    <div className="flex gap-2 ml-auto">
                        <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-gray-300">
                            Cancelar
                        </button>
                        <button onClick={handleSubmit} disabled={submitting}
                            className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                            {submitting ? 'Registrando...' : 'Confirmar Baixa'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;
